import React, { useEffect, useCallback } from 'react';
import { IonIcon } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import type { DashboardWidget } from '../../../services/dashboard';
import WidgetRenderer from '../../WidgetRenderer';
import './WidgetFullscreenOverlay.css';

export interface WidgetFullscreenOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  widget: DashboardWidget | null;
  householdId: string;
}

const WidgetFullscreenOverlay: React.FC<WidgetFullscreenOverlayProps> = ({
  isOpen,
  onClose,
  widget,
  householdId,
}) => {
  // Handle escape key to close
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when overlay is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !widget) {
    return null;
  }

  return (
    <div
      className="widget-fullscreen-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={`${widget.title || widget.widget_type} fullscreen view`}
    >
      <div className="widget-fullscreen-backdrop" />

      <div className="widget-fullscreen-container">
        {/* Close button */}
        <button
          className="widget-fullscreen-close"
          onClick={onClose}
          aria-label="Close fullscreen"
        >
          <IonIcon icon={closeOutline} />
        </button>

        {/* Widget title */}
        {widget.title && (
          <div className="widget-fullscreen-title">
            {widget.title}
          </div>
        )}

        {/* Widget content */}
        <div className="widget-fullscreen-content">
          <WidgetRenderer
            widget={widget}
            householdId={householdId}
            editMode={false}
            isFullscreen
          />
        </div>
      </div>
    </div>
  );
};

export default WidgetFullscreenOverlay;
